import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { CheckCheck, ChevronDown, X } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { usePapaParse } from 'react-papaparse';
import { useForm, Controller } from 'react-hook-form';
import { RangeDatepicker } from 'chakra-dayzed-datepicker';
import { Select } from 'chakra-react-select';

import { djs } from '@duke-hq/djs';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

type FormData = {
  dateRange: Date[];
  providers: { value: string; label: string }[];
};

export function PendingReferralsReport() {
  const toast = useToast();
  const { jsonToCSV } = usePapaParse();

  const defaultEndDate = new Date();
  const defaultStartDate = new Date(defaultEndDate);
  defaultStartDate.setDate(defaultStartDate.getDate() - 7);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      dateRange: [defaultStartDate, defaultEndDate],
      providers: [],
    },
    mode: 'onChange',
  });

  const watchedValues = watch();

  // Provider options
  const { data: tenants } = trpcAdmin.providers.all.useQuery(
    {
      filters: { isUsingNewReferralProcess: true },
    },
    { initialData: [] },
  );

  const providerOptions = useMemo(() => {
    return tenants
      .map((tenant) => ({
        value: `${tenant.id}`,
        label: `${tenant.name}${tenant.is_billable ? ' (billable)' : ''}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tenants]);

  const setDateRangeFromNow = useCallback(
    (days: number) => {
      const end = new Date();
      const start = new Date(end);
      start.setDate(start.getDate() - days);
      setValue('dateRange', [start, end]);
    },
    [setValue],
  );

  const handleSelectAllProviders = useCallback(() => {
    setValue('providers', providerOptions);
  }, [providerOptions, setValue]);

  const handleClearProviders = useCallback(() => {
    setValue('providers', []);
  }, [setValue]);

  const downloadPendingReferralReport =
    trpcAdmin.enrolment.downloadPendingReferralReport.useMutation({
      onSuccess: (data) => {
        if (data.length === 0) {
          toast({
            title: t`No pending referrals found for the selected criteria`,
            status: 'info',
            duration: 5000,
          });
        } else {
          toast({
            title: t`Report generated successfully`,
            description: t`Your CSV report is being downloaded`,
            status: 'success',
            duration: 5000,
          });
        }
      },
      onError: (error) => {
        toast({
          title: t`Failed to generate report`,
          description: error.message,
          status: 'error',
          duration: 5000,
        });
      },
    });

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (data.dateRange.length !== 2 || data.providers.length === 0) {
        return;
      }

      const [fromDate, toDate] = data.dateRange;
      if (!fromDate || !toDate) {
        return;
      }

      try {
        const response = await downloadPendingReferralReport.mutateAsync({
          fromDate,
          toDate,
          providerIds: data.providers.map((p) => p.value),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });

        if (response.length === 0) {
          return;
        }

        // Convert to CSV and download
        const formattedData = response.map((r) => ({
          'Course Code': r.courseCode,
          'Course Title': r.courseTitle,
          'Purchase Order': r.purchaseOrder,
          'Created At': r.createdAt,
          'Activation code expiry': r.activationCodeExpiry,
          'Contract Type': r.contractType,
          'Contract Category': r.contractCategory,
          'Course Segment': r.courseSegment,
          'Consultant Name': r.consultantName,
          'Consultant Email': r.consultantEmail,
          'Site Location': r.siteLocation,
          State: r.state,
          ...(r.providerName != null ? { 'Provider Name': r.providerName } : {}),
        }));

        const csvData = jsonToCSV(formattedData);
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        let filename = `pending_referrals_report_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}`;
        if (data.providers.length === 1) {
          const providerName = data.providers[0]?.label.replace(/[^a-zA-Z0-9]/g, '_') ?? 'provider';
          filename = `${providerName}-${filename}`;
        }
        filename += '.csv';

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast({
          title:
            error instanceof Error
              ? error.message
              : t`An error occurred while generating the report`,
          status: 'error',
          duration: 5000,
        });
      }
    },
    [downloadPendingReferralReport, jsonToCSV, toast],
  );

  return (
    <VStack alignItems="start" w="full" p={4}>
      <Text key="page-heading" textStyle="large" fontWeight="medium">
        <Trans>Pending referrals</Trans>
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap={8} h="full">
          <VStack alignItems="flex-start" justifyContent="start" flexWrap="wrap">
            <Box key="date-range">
              <Controller
                name="dateRange"
                control={control}
                rules={{
                  required: t`Date range is required`,
                  validate: (value) => {
                    if (value.length !== 2) {
                      return t`Please select both start and end dates`;
                    }
                    if (value[0] && value[1] && value[0] >= value[1]) {
                      return t`Start date must be before end date`;
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <FormControl isInvalid={!!errors.dateRange} isRequired>
                    <FormLabel fontSize="sm" mb={1}>
                      <Trans>Referral created on (date range)</Trans>
                    </FormLabel>
                    <ButtonGroup isAttached variant="outline" maxW="fit-content">
                      <RangeDatepicker
                        selectedDates={field.value}
                        onDateChange={(dates) => {
                          if (dates.length === 2 && dates[0] && dates[1]) {
                            // Set start date to the very beginning of the first day
                            const startDate = new Date(dates[0]);
                            startDate.setHours(0, 0, 0, 0);

                            // Set end date to the very end of the last day
                            const endDate = new Date(dates[1]);
                            endDate.setHours(23, 59, 59, 999);

                            field.onChange([startDate, endDate]);
                          } else {
                            field.onChange(dates);
                          }
                        }}
                        maxDate={defaultEndDate}
                        configs={{
                          dateFormat: 'dd/MM/yyyy',
                        }}
                      />
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label={t`Date range select options`}
                          icon={<Icon as={ChevronDown} boxSize={4} />}
                          borderLeftRadius={0}
                        />
                        <MenuList>
                          <MenuItem onClick={() => setDateRangeFromNow(1)}>
                            <Trans>Last 24 hours</Trans>
                          </MenuItem>
                          <MenuItem onClick={() => setDateRangeFromNow(3)}>
                            <Trans>Last 3 days</Trans>
                          </MenuItem>
                          <MenuItem onClick={() => setDateRangeFromNow(7)}>
                            <Trans>Last 7 days</Trans>
                          </MenuItem>
                          <MenuItem onClick={() => setDateRangeFromNow(30)}>
                            <Trans>Last 30 days</Trans>
                          </MenuItem>
                          <MenuItem onClick={() => setDateRangeFromNow(60)}>
                            <Trans>Last 60 days</Trans>
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </ButtonGroup>
                    <FormErrorMessage>{errors.dateRange?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />
            </Box>

            <VStack key="provider-selector" spacing={2} align="stretch" minW="320px">
              <Controller
                name="providers"
                control={control}
                rules={{
                  required: t`At least one provider must be selected`,
                  validate: (value) => {
                    if (value.length === 0) {
                      return t`Please select at least one provider`;
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <FormControl isInvalid={!!errors.providers} isRequired>
                    <FormLabel fontSize="sm" mb={1}>
                      <Trans>Providers</Trans>
                    </FormLabel>
                    <Select
                      isMulti
                      isSearchable
                      placeholder={t`Select providers...`}
                      options={providerOptions}
                      value={field.value}
                      onChange={field.onChange}
                      closeMenuOnSelect={false}
                    />
                    <FormHelperText>
                      <Trans>Only providers with the new referral process are shown.</Trans>
                    </FormHelperText>
                    <FormErrorMessage>{errors.providers?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />
              <HStack spacing={4}>
                <Button
                  size="xs"
                  variant="link"
                  onClick={handleSelectAllProviders}
                  isDisabled={watchedValues.providers?.length === providerOptions.length}
                  leftIcon={<Icon as={CheckCheck} />}
                >
                  <Trans>Select All</Trans>
                </Button>
                <Button
                  size="xs"
                  variant="link"
                  onClick={handleClearProviders}
                  isDisabled={watchedValues.providers?.length === 0}
                  leftIcon={<Icon as={X} />}
                >
                  <Trans>Clear All</Trans>
                </Button>
              </HStack>
            </VStack>
          </VStack>
          <Button
            type="submit"
            variant="primary"
            isLoading={downloadPendingReferralReport.isPending}
            width="fit-content"
          >
            <Trans>Download CSV</Trans>
          </Button>
        </Flex>
      </form>
    </VStack>
  );
}
